if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');
        this.confirmedQuantity = this.input?.dataset.cartQuantity ?? this.input?.getAttribute('value') ?? '0';
        this.quantityEditVersion = 0;

        const debouncedOnChange = debounce((event) => {
          try {
            if (parseInt(event.target.value) === 0) {
              this.startQueue(event.target.dataset.index, parseInt(event.target.value));
            } else {
              this.validateQuantity(event);
            }
          } finally {
            this.homeChangePending = false;
            this.homeInputDirty = false;
            this.flushHomeCartUpdate();
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', (event) => {
          this.homeChangePending = true;
          this.quantityEditVersion += 1;
          debouncedOnChange(event);
        });
        this.addEventListener('input', () => {
          this.quantityEditVersion += 1;
          this.homeInputDirty = this.input?.value !== this.input?.getAttribute('value');
          this.clearQuantityUpdateError();
        });
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        this.addEventListener('focusout', () => {
          queueMicrotask(() => this.flushHomeCartUpdate());
        });
      }

      connectedCallback() {
        if (this.isItemsHomeCard && window.ItemsCatalogCards?.currentCart) {
          this.applyHomeCartUpdate(window.ItemsCatalogCards.currentCart);
        }
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const cartData = event.cartData;
          if (Array.isArray(cartData?.items) && (event.source === 'quick-add' || 'item_count' in cartData || 'token' in cartData)) {
            this.applyHomeCartUpdate(cartData);
            return;
          }
          if (this.isItemsHomeCard) {
            const variantId = Number(this.dataset.index);
            if (cartData?.variant_id && Number(cartData.variant_id) !== variantId) return;
            if (Array.isArray(cartData?.items) && !cartData.items.some(item => Number(item.variant_id || item.id) === variantId)) return;
            this.onCartUpdate().catch(() => {});
            return;
          }
          if (
            event.source === 'quick-add' ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          }).catch(() => {});
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get isItemsHomeCard() {
        return Boolean(this.closest('items-home-catalog, items-home-motion'));
      }

      get homeInputBusy() {
        return this.homeChangePending || this.homeInputDirty || this.requestStarted || this.queue.length > 0 || document.activeElement === this.input;
      }

      get canonicalId() {
        return this.dataset.canonicalId || `quick-add-bulk-${this.dataset.index}-${this.sectionId}`;
      }

      applyHomeCartUpdate(cartData) {
        if (!this.input) return;
        const quantity = this.rememberCartQuantity(cartData);
        if (this.homeInputBusy) {
          this.pendingHomeCartData = cartData;
          return;
        }
        this.pendingHomeCartData = null;
        this.setConfirmedQuantity(quantity);
      }

      flushHomeCartUpdate() {
        if (!this.isConnected || this.homeInputBusy) return;
        if (this.pendingHomeCartData) this.applyHomeCartUpdate(this.pendingHomeCartData);
        if (this.pendingHomeRefresh) {
          this.pendingHomeRefresh = false;
          this.onCartUpdate().catch(() => {});
        }
      }

      replaceHomeQuantity(sourceQty) {
        if (this.homeInputBusy) {
          this.pendingHomeRefresh = true;
          return;
        }
        const ids = new Map();
        const fragment = sourceQty.cloneNode(true);
        fragment.querySelectorAll('[id]').forEach(element => {
          const originalId = element.id;
          const instanceId = `${this.id}-${originalId}`;
          ids.set(originalId, instanceId);
          element.dataset.originalInputId = originalId;
          element.id = instanceId;
        });
        fragment.querySelectorAll('*').forEach(element => {
          ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attribute => {
            if (!element.hasAttribute(attribute)) return;
            element.setAttribute(attribute, element.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
          });
        });
        this.innerHTML = fragment.innerHTML;
        this.quantity = this.querySelector('quantity-input');
        this.confirmedQuantity = this.input?.dataset.cartQuantity ?? this.input?.getAttribute('value') ?? this.confirmedQuantity;
        this.listenForActiveInput();
        this.listenForKeydown();
      }

      resetQuantityInput(id) {
        if (!this.isItemsHomeCard) return super.resetQuantityInput(id);
        const input = this.input;
        if (input && String(input.dataset.index) === String(id)) input.value = input.getAttribute('value');
        this.isEnterPressed = false;
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          const input = this.input;
          if (!this.isItemsHomeCard || this.homeFocusBoundInput !== input) {
            input?.addEventListener('focusin', (event) => event.target.select());
            if (this.isItemsHomeCard) this.homeFocusBoundInput = input;
          }
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        const input = this.input;
        if (this.isItemsHomeCard && this.homeKeydownBoundInput === input) return;
        if (this.isItemsHomeCard) this.homeKeydownBoundInput = input;
        input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (this.dataset.sourceSectionId) return this.dataset.sourceSectionId;
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        if (this.querySelector('[data-quantity-update-error]')) {
          this.pendingHomeRefresh = false;
          return Promise.resolve();
        }
        if (this.homeInputBusy) {
          this.pendingHomeRefresh = true;
          return Promise.resolve();
        }
        const editVersion = this.quantityEditVersion;
        return new Promise((resolve, reject) => {
          fetch(this.getSectionRequestUrl())
            .then((response) => response.text())
            .then((responseText) => {
              if (editVersion !== this.quantityEditVersion || this.querySelector('[data-quantity-update-error]')) {
                resolve();
                return;
              }
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.getElementById(this.isItemsHomeCard ? this.canonicalId : `quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                if (this.homeInputBusy) {
                  this.pendingHomeRefresh = true;
                  resolve();
                  return;
                }
                if (this.isItemsHomeCard) this.replaceHomeQuantity(sourceQty);
                else {
                  this.innerHTML = sourceQty.innerHTML;
                  this.confirmedQuantity = this.input?.dataset.cartQuantity ?? this.input?.getAttribute('value') ?? this.confirmedQuantity;
                }
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        if (this.dataset.sourceUrl) return this.dataset.sourceUrl;
        const bulkContainer = this.closest('.collection-quick-add-bulk');

        if (bulkContainer?.dataset.url && bulkContainer?.dataset.productId) {
          const separator = bulkContainer.dataset.url.includes('?') ? '&' : '?';
          return `${bulkContainer.dataset.url}${separator}product_id=${bulkContainer.dataset.productId}`;
        }

        return `${window.location.pathname}${window.location.search}`;
      }

      getSectionRequestUrl() {
        const sectionsUrl = this.getSectionsUrl();
        const separator = sectionsUrl.includes('?') ? '&' : '?';
        return `${sectionsUrl}${separator}section_id=${this.sectionId}`;
      }

      rememberCartQuantity(cartData) {
        const variantId = Number(this.dataset.index);
        const quantity = cartData.items.reduce((sum, item) => sum + (Number(item.variant_id || item.id) === variantId ? Number(item.quantity || 0) : 0), 0);
        this.confirmedQuantity = String(quantity);
        return quantity;
      }

      setConfirmedQuantity(quantity) {
        this.confirmedQuantity = String(quantity);
        if (!this.input) return;
        this.input.value = this.confirmedQuantity;
        this.input.setAttribute('value', this.confirmedQuantity);
        this.input.dataset.cartQuantity = this.confirmedQuantity;
        this.querySelector('quantity-input')?.validateQtyRules?.();
      }

      clearQuantityUpdateError() {
        const error = this.querySelector('[data-quantity-update-error]');
        if (!error) return;
        const input = this.input;
        if (input) {
          const descriptions = (input.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== error.id);
          if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
          else input.removeAttribute('aria-describedby');
          input.removeAttribute('aria-invalid');
        }
        error.remove();
      }

      showQuantityUpdateError(items, message, allowRetry = true) {
        this.clearQuantityUpdateError();
        const error = document.createElement('div');
        error.id = `${this.id}-quantity-update-error`;
        error.dataset.quantityUpdateError = '';
        error.className = 'quick-add-bulk__error caption';
        error.setAttribute('role', 'alert');
        error.textContent = message || 'No pudimos confirmar la cantidad. Revisá el carrito o volvé a intentarlo. ';
        if (!allowRetry) {
          const cartLink = document.createElement('a');
          cartLink.href = routes.cart_url || '/cart';
          cartLink.textContent = 'Editar en el carrito';
          error.append(cartLink);
        }
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--tertiary';
        retry.textContent = 'Reintentar';
        retry.addEventListener('click', () => {
          if (this.requestStarted || this.queue.length) return;
          const desiredQuantity = items[this.dataset.index];
          if (desiredQuantity === undefined || !this.input) return;
          this.clearQuantityUpdateError();
          this.input.value = String(desiredQuantity);
          this.quantityEditVersion += 1;
          if (Number(desiredQuantity) === 0) this.startQueue(this.dataset.index, 0);
          else this.validateQuantity({ target: this.input });
        });
        if (allowRetry) error.append(retry);
        this.append(error);
        if (this.input) {
          this.input.setAttribute('aria-invalid', 'true');
          this.input.setAttribute('aria-describedby', `${this.input.getAttribute('aria-describedby') || ''} ${error.id}`.trim());
        }
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');
        const editVersion = this.quantityEditVersion;
        const ids = Object.keys(items);
        return fetch(`${routes.cart_url || '/cart'}.js`, { cache: 'no-store' })
          .then(async (response) => {
            if (!response.ok) throw new Error('Cart could not be read');
            const cartData = await response.json();
            if (!Array.isArray(cartData.items)) throw new Error('Cart could not be read');
            this.rememberCartQuantity(cartData);
            const updates = {};
            for (const [variantId, quantity] of Object.entries(items)) {
              const lines = cartData.items.filter(item => Number(item.variant_id || item.id) === Number(variantId));
              if (lines.length > 1) {
                const error = new Error('This variant has separate cart lines');
                error.ambiguousCartData = cartData;
                throw error;
              }
              updates[lines[0]?.key || variantId] = quantity;
            }
            const body = JSON.stringify({
              updates,
              sections: this.getSectionsToRender().map((section) => section.section),
              sections_url: this.getSectionsUrl(),
            });
            return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          })
          .then(async (response) => {
            const parsedState = JSON.parse(await response.text());
            if (!response.ok || parsedState.errors || parsedState.status || !Array.isArray(parsedState.items)) {
              throw new Error('Quantity update could not be confirmed');
            }
            this.rememberCartQuantity(parsedState);
            this.renderSections(parsedState, ids);
            this.clearQuantityUpdateError();
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch(async (error) => {
            if (editVersion === this.quantityEditVersion) {
              this.showQuantityUpdateError(
                items,
                error.ambiguousCartData ? 'Este producto tiene líneas separadas en el carrito. Editá cada línea allí para conservar sus comentarios y opciones. ' : undefined,
                !error.ambiguousCartData
              );
            }
            try {
              let cartData = error.ambiguousCartData;
              if (!cartData) {
                const response = await fetch(`${routes.cart_url || '/cart'}.js`, { cache: 'no-store' });
                if (!response.ok) throw new Error('Cart could not be read');
                cartData = await response.json();
              }
              if (!Array.isArray(cartData.items)) throw new Error('Cart could not be read');
              const quantity = this.rememberCartQuantity(cartData);
              if (editVersion === this.quantityEditVersion && this.queue.length === 0) {
                this.pendingHomeCartData = null;
                this.setConfirmedQuantity(quantity);
                this.homeInputDirty = false;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData });
            } catch {
              if (editVersion === this.quantityEditVersion && this.queue.length === 0) {
                this.pendingHomeCartData = null;
                this.setConfirmedQuantity(this.confirmedQuantity);
                this.homeInputDirty = false;
              }
            }
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
            this.flushHomeCartUpdate();
          });
      }

      getSectionsToRender() {
        return [
          {
            id: this.isItemsHomeCard ? this.id : `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#${this.isItemsHomeCard ? this.canonicalId : `quick-add-bulk-${this.dataset.index}-${this.sectionId}`}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          if (!this.isItemsHomeCard && section.id === `quick-add-bulk-${this.dataset.index}-${this.sectionId}` &&
              (this.homeChangePending || this.homeInputDirty || this.queue.length > 0 || document.activeElement === this.input)) {
            this.pendingHomeRefresh = true;
            return;
          }
          if (this.isItemsHomeCard && section.id === this.id) {
            if (!parsedState.sections?.[section.section]) return;
            const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
            const sourceQty = html.getElementById(this.canonicalId);
            if (sourceQty) this.replaceHomeQuantity(sourceQty);
            return;
          }
          if (this.isItemsHomeCard && !parsedState.sections?.[section.section]) return;
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
            if (section.id === `quick-add-bulk-${this.dataset.index}-${this.sectionId}`) {
              this.confirmedQuantity = this.input?.dataset.cartQuantity ?? this.input?.getAttribute('value') ?? this.confirmedQuantity;
            }
          }
        });

        if (this.isEnterPressed) {
          if (this.isItemsHomeCard) this.input?.select();
          else this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
